import { useState } from "react";

import { Button, Icon, InputField, SettingsList, SettingsListItem, Text, View } from "../base";
import { Dialog } from "../base/Dialog";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";
import { sessionActions } from "../../redux/reducers/sessionSlice";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useAppDispatch } from "../../redux";

type AccountCardProps = {
  userId: string | undefined;
  email: string | undefined;
  name: string | undefined;
};

export const AccountCard = ({ userId, email, name }: AccountCardProps) => {
  const dispatch = useAppDispatch();
  const updateRequest = useApiRequest();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [draftName, setDraftName] = useState(name ?? "");
  const [error, setError] = useState<string | null>(null);

  const openEdit = () => {
    setDraftName(name ?? "");
    setError(null);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    if (updateRequest.isLoading) return;
    setIsEditOpen(false);
  };

  const onSave = async () => {
    const nextName = draftName.trim();
    if (!userId || !nextName || nextName === name) return;
    const { error: reqError } = await updateRequest.fetch("/v1/me", {
      method: "PATCH",
      body: { name: nextName },
    });
    if (reqError) {
      setError(reqError.message || "Could not update name");
      return;
    }
    await dispatch(sessionActions.getSession());
    setIsEditOpen(false);
  };

  return (
    <OrganizationSettingsTab title="Account">
      <SettingsList>
        <SettingsListItem
          title={name || "No name set"}
          subtitle="Name"
          icon={name ? name.charAt(0).toUpperCase() : "U"}
          right={
            userId ? (
              <button
                type="button"
                onClick={openEdit}
                title="Edit name"
                className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Icon
                  name="edit"
                  size={14}
                />
              </button>
            ) : undefined
          }
        />
        <SettingsListItem
          title={email || "No email"}
          subtitle="Email Address"
          icon="mail"
        />
      </SettingsList>

      <Dialog
        isOpen={isEditOpen}
        onClose={closeEdit}
        title="Edit Name"
      >
        <View className="p-4 space-y-4">
          <Text variant="body">Update the name displayed on your profile:</Text>
          <InputField
            value={draftName}
            onChange={setDraftName}
            placeholder="Your name"
            className="w-full"
          />
          {error && <Text className="text-xs text-red-500 dark:text-red-400">{error}</Text>}
          <View className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              onClick={closeEdit}
              isDisabled={updateRequest.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={onSave}
              isDisabled={updateRequest.isLoading || !draftName.trim() || draftName.trim() === name}
            >
              {updateRequest.isLoading ? "Saving…" : "Save"}
            </Button>
          </View>
        </View>
      </Dialog>
    </OrganizationSettingsTab>
  );
};
